import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useLogger } from '../composables/useLogger'
import { useSupabaseClientStore } from './supaBaseClientStore'
import { useAuthStore } from './authStore'
import { usePostHog, ONBOARDING_EVENTS } from '../composables/usePostHog'
import { useFirebaseAnalytics } from '../composables/useFirebaseAnalytics'
import { LOAD_CALENDAR_EXAMPLE } from '../src/constants'

export type WizardStatus = 'not_relevant' | 'in_progress' | 'done'

export interface WizardTileStatus {
  status: WizardStatus
  doneAt: Date | null
}

interface WizardState {
  calendar: WizardTileStatus
  album: WizardTileStatus
  jigsaw: WizardTileStatus
  qrcode: WizardTileStatus
  overall_status: WizardStatus
}

// for use in the authStore during registration
export async function initializeStartWizardStatus(userId: string) {
  const logger = useLogger()
  logger.debug('SW: Initializing start wizard status for user', { userId })
  const supabase = useSupabaseClientStore().supabase
  const { error } = await supabase
    .from('start_wizard_status')
    .insert([{
      user_id: userId,
      calendar_status: 'in_progress',
      album_status: 'in_progress',
      jigsaw_status: 'in_progress',
      qrcode_status: 'in_progress',
      overall_status: 'in_progress',
      created_at: new Date(),
    }])

  if (error) logger.throwSupabaseError(error, `Failed to initialize start wizard status for user ${userId}`)
}

export const useStartWizardStore = defineStore('startWizard', () => {
  const logger = useLogger()
  const supabaseClientStore = useSupabaseClientStore()
  const authStore = useAuthStore()
  const posthog = usePostHog()
  const analytics = useFirebaseAnalytics()
  const userId = computed(() => authStore.userId)

  const status = ref<WizardState>({
    calendar: { status: 'in_progress', doneAt: null },
    album: { status: 'in_progress', doneAt: null },
    jigsaw: { status: 'in_progress', doneAt: null },
    qrcode: { status: 'in_progress', doneAt: null },
    overall_status: 'in_progress'
  })

  const isLoaded = ref(false)

  // Add this function to handle store reset
  function $reset() {
    logger.debug('useStartWizardStore: $reset', { userId: userId.value })
    isLoaded.value = false
    status.value = {
      calendar: { status: 'in_progress', doneAt: null },
      album: { status: 'in_progress', doneAt: null },
      jigsaw: { status: 'in_progress', doneAt: null },
      qrcode: { status: 'in_progress', doneAt: null },
      overall_status: 'in_progress'
    }
  }

  watch(userId, () => {
    logger.debug('useStartWizardStore: userId changed, resetting store', { userId: userId.value })
    $reset()
    loadStatus()
  })

  // Getters
  const hasInProgressTiles = computed(() => {
    return Object.values(status.value).some(s =>
      typeof s === 'object' && s.status === 'in_progress'
    )
  })

  // Actions
  async function loadStatus() {
    logger.debug('useStartWizardStore: loadStatus', { userId: userId.value })
    if (!userId.value) {
      logger.debug('useStartWizardStore: No userId, skipping loadStatus')
      return
    }
    const supabase = supabaseClientStore.supabase
    const { data, error } = await supabase
      .from('start_wizard_status')
      .select('*')
      .eq('user_id', userId.value)
      .maybeSingle()

    if (error) {
      logger.throwSupabaseError(error, `Failed to load start wizard status for user ${userId.value}`)
    }

    if (!data) {
      logger.debug('SW: No rows found, setting overall status to not_relevant')
      status.value = {
        calendar: { status: 'not_relevant', doneAt: null },
        album: { status: 'not_relevant', doneAt: null },
        jigsaw: { status: 'not_relevant', doneAt: null },
        qrcode: { status: 'not_relevant', doneAt: null },
        overall_status: 'not_relevant'
      }
    } else {
      status.value = {
        calendar: {
          status: data.calendar_status,
          doneAt: data.calendar_done_at ? new Date(data.calendar_done_at) : null
        },
        album: {
          status: data.album_status,
          doneAt: data.album_done_at ? new Date(data.album_done_at) : null
        },
        jigsaw: {
          status: data.jigsaw_status,
          doneAt: data.jigsaw_done_at ? new Date(data.jigsaw_done_at) : null
        },
        qrcode: {
          status: data.qrcode_status,
          doneAt: data.qrcode_done_at ? new Date(data.qrcode_done_at) : null
        },
        overall_status: data.overall_status
      }
    }
    isLoaded.value = true
  }

  async function updateTileStatus(tile: keyof Omit<WizardState, 'overall_status'>, newStatus: WizardTileStatus) {
    logger.debug('SW: Updating tile status', { tile, newStatus })
    const supabase = supabaseClientStore.supabase
    const updates: Record<string, any> = {
      [`${tile}_status`]: newStatus.status,
      [`${tile}_done_at`]: newStatus.doneAt
    }
    logger.debug('SW: Updates', { updates })

    const { error } = await supabase
      .from('start_wizard_status')
      .update(updates)
      .eq('user_id', userId.value)

    if (error) logger.throwSupabaseError(error, `Failed to update ${tile} status for user ${userId.value}`)

    status.value[tile] = newStatus

    // Update overall status
    updateOverallStatus()
  }

  async function updateOverallStatus() {
    const allTilesDone = Object.entries(status.value)
      .filter(([key]) => key !== 'overall_status')
      .every(([, value]) => typeof value === 'object' && (value.status === 'done' || value.status === 'not_relevant'))

    const allTilesNotRelevant = Object.entries(status.value)
      .filter(([key]) => key !== 'overall_status')
      .every(([, value]) => typeof value === 'object' && value.status === 'not_relevant')

    let newOverallStatus: WizardStatus = 'in_progress'
    if (allTilesNotRelevant) {
      newOverallStatus = 'not_relevant'
    } else if (allTilesDone) {
      newOverallStatus = 'done'
    }

    if (newOverallStatus !== status.value.overall_status) {
      const supabase = supabaseClientStore.supabase
      const { error } = await supabase
        .from('start_wizard_status')
        .update({ overall_status: newOverallStatus })
        .eq('user_id', userId.value)

      if (error) logger.throwSupabaseError(error, `Failed to update overall status for user ${userId.value}`)
      status.value.overall_status = newOverallStatus
    }
  }

  async function dismissTile(tile: keyof Omit<WizardState, 'overall_status'>) {
    logger.debug('SW: Dismissing tile', { tile })
    await updateTileStatus(tile, {
      status: 'not_relevant',
      doneAt: new Date()
    })
  }

  async function checkTileStatus(tile: keyof Omit<WizardState, 'overall_status'>): Promise<boolean> {
    const supabase = supabaseClientStore.supabase

    logger.debug('SW: Checking tile status', { tile })

    const checkFunctions: Record<keyof Omit<WizardState, 'overall_status'>, () => Promise<boolean>> = {
      calendar: async () => {
        logger.debug('SW: Checking calendar tile status')
        // First get all calendars for the user
        const { data: calendars } = await supabase
          .from('calendars')
          .select('id')
          .eq('user_id', userId.value)

        if (!calendars?.length) {
          logger.debug('SW: No calendars found')
          return false
        }


        // Then check for any events in these calendars
        const calendarIds = calendars.map(cal => cal.id)
        logger.debug('SW: Calendar IDs', { calendarIds })
        const { data: events } = await supabase
          .from('calendar_items')
          .select('id')
          .in('calendar_id', calendarIds)
          .limit(2)

        const numRequiredEvents = LOAD_CALENDAR_EXAMPLE ? 2 : 1
        const numEvents = events?.length ?? 0
        const hasEvents = numEvents >= numRequiredEvents
        logger.debug('SW: Calendar check result', { numRequiredEvents: numRequiredEvents, numEvents: numEvents, hasEvents: hasEvents })
        return hasEvents
      },
      album: async () => {
        logger.debug('SW: Checking album tile status')
        const { data } = await supabase
          .from('albumimages')
          .select('id')
          .eq('user_id', userId.value)
          .limit(1)

        const hasAlbumImages = (data?.length ?? 0) > 0
        logger.debug('SW: Album check result', { hasAlbumImages })
        return hasAlbumImages
      },
      jigsaw: async () => {
        logger.debug('SW: Checking jigsaw tile status')
        const { data, error } = await supabase
          .from('imagegames')
          .select('id')
          .eq('user_id', userId.value)
          .eq('game_type', 'Puzzle')
          .limit(1)

        const hasJigsaws = (data?.length ?? 0) > 0
        logger.debug('SW: Jigsaw check result', { hasJigsaws })
        if (error) throw error
        return hasJigsaws
      },
      qrcode: async () => {
        logger.debug('SW: Checking qrcode tile status')
        const { data, error } = await supabase
          .from('qr_codes_senior')
          .select('id')
          .eq('user_id', userId.value)
          .not('used_at', 'is', null)
          .limit(1)

        const hasUsedQrCode = (data?.length ?? 0) > 0
        logger.debug('SW: QR code check result', { hasUsedQrCode })
        if (error) logger.throwSupabaseError(error, `Failed to check qrcode status for user ${userId.value}`)
        return hasUsedQrCode
      }
    }

    try {
      const isDone = await checkFunctions[tile]()
      if (isDone) {
        await updateTileStatus(tile, {
          status: 'done',
          doneAt: new Date()
        })
        await posthog.capture(ONBOARDING_EVENTS.START_WIZARD_TILE_COMPLETED, {
          tile: tile,
        })
        await analytics.logEvent(ONBOARDING_EVENTS.START_WIZARD_TILE_COMPLETED, {
          tile: tile,
        })
      }
      return isDone
    } catch (error) {
      logger.logErrorCaught('Error checking tile status', 'StartWizardStore.ts', error)
      throw error
    }
  }

  async function checkAllInProgressTiles() {
    logger.debug('SW: Checking all in progress tiles')
    const inProgressTiles = Object.entries(status.value)
      .filter(([key, value]) =>
        key !== 'overall_status' &&
        typeof value === 'object' &&
        value.status === 'in_progress'
      )
      .map(([key]) => key as keyof Omit<WizardState, 'overall_status'>)

    for (const tile of inProgressTiles) {
      await checkTileStatus(tile)
    }
  }

  return {
    status,
    isLoaded,
    hasInProgressTiles,
    loadStatus,
    updateTileStatus,
    dismissTile,
    checkTileStatus,
    checkAllInProgressTiles
  }
}) 